import { Dialog, Transition } from '@headlessui/react';
import { Fragment, ReactNode, useMemo } from 'react';
import { Overlay } from '../overlay';
import { classNames } from '@cotera/client/app/components/utils';
import { Button } from '../button';
import { useKeyPress } from '@cotera/client/app/hooks/use-key-press';

/**
 * Detects if we're in v1 mode by checking for .v1-app-shell in the DOM.
 * This allows the modal to inherit v1's CSS variable overrides.
 */
const useIsV1Mode = () => {
  return useMemo(() => {
    if (typeof document === 'undefined') {
      return false;
    }
    return document.querySelector('.v1-app-shell') !== null;
  }, []);
};

const PRIORITY = {
  low: 'z-10',
  medium: 'z-20',
  high: 'z-30',
  highest: 'z-40',
};

const SIZE = {
  default: '',
  large: 'md:max-w-[800px] md:max-h-[600px]',
  xlarge: 'md:max-w-[1000px] md:max-h-[700px]',
};

export const Modal: React.FC<{
  open: boolean;
  onClose?: (val: boolean) => void;
  children: ReactNode;
  center?: boolean;
  className?: string;
  priority?: keyof typeof PRIORITY;
  padding?: boolean;
  contentClassName?: string;
  closeEnabled?: boolean;
  size?: keyof typeof SIZE;
}> = ({
  open,
  onClose = () => {},
  children,
  center = true,
  className,
  priority = 'medium',
  padding = true,
  contentClassName,
  closeEnabled = true,
  size = 'default',
}) => {
  const isV1 = useIsV1Mode();

  useKeyPress('Escape', () => {
    onClose(false);
  });

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className={classNames(
          'relative',
          PRIORITY[priority],
          isV1 && 'v1-app-shell'
        )}
        onClose={() => {
          onClose(false);
          onClose(false);
        }}
      >
        <Overlay z={PRIORITY[priority]} />

        <div
          onClick={(event) => {
            //ensure that the click event **started** on the overlay, not the child
            if (event.target === event.currentTarget) {
              onClose(false);
            }
          }}
          className={classNames(
            'fixed inset-0 overflow-y-auto',
            PRIORITY[priority]
          )}
        >
          <div
            className={classNames(
              'flex min-h-full text-center',
              center
                ? 'justify-center items-center'
                : 'justify-center items-start pt-[min(18vh,7.5rem)] pb-8',
              className,
              padding ? (center ? 'p-0 md:p-4' : 'p-0 md:px-4 md:pb-4') : ''
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={classNames(
                  'grow-0 relative transform overflow-hidden bg-background text-left shadow-xl',
                  'h-full md:h-fit rounded-2xl md:my-8',
                  SIZE[size],
                  contentClassName ?? ''
                )}
              >
                {closeEnabled && (
                  <Button
                    icon="x-mark"
                    tooltip="left"
                    iconOnly
                    inline
                    text="Close"
                    className="absolute right-2 top-2"
                    onClick={() => onClose(false)}
                  />
                )}
                {/* No transition on width/height: Motion layout (e.g. modal-manager) owns size animations */}
                <div className="w-full h-full md:w-fit md:h-fit">
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
